import { useState, useEffect, useCallback, useMemo, Fragment } from 'react';
import { ChevronDown, ExternalLink, Loader2 } from 'lucide-react';
import Notice from '../../components/common/Notice';
import { apiService } from '../../services/api.service';
import { API_ENDPOINTS } from '../../config/environment';
import { formatTL } from '../../utils/money';
import { getWeekDateRange } from '../../utils/payoutWeek';
import { fleetToast } from '../../utils/toast';
import './IssuedInvoices.css';

/**
 * Hızlıyo'nun filoya KESTİĞİ faturalar (entegrasyon bedeli ve filoya kesilen diğer faturalar).
 *
 * Hakediş faturasının TERS yönü: orada faturayı filo keser, burada Hızlıyo keser ve filo öder.
 * Yalnız GİB'e gönderilmiş belgeler gelir; Hızlıyo'nun taslakları filoya gösterilmez.
 *
 * @param year, week Verilirse yalnız o haftayla ÇAKIŞAN dönemli faturalar listelenir ve hiç
 *        yoksa bileşen hiçbir şey çizmez (hakediş sekmesindeki kısa görünüm). Verilmezse tümü.
 */
const STATUS = {
    2: { label: 'Kesildi', tone: 'info' },
    4: { label: 'İptal edildi', tone: 'error' },
    5: { label: 'İade edildi', tone: 'warning' },
};

const DOC_TYPE = { 1: 'e-Fatura', 2: 'e-Arşiv' };

// DateOnly ("2026-10-05") yerel saat diliminden bağımsız okunur; new Date(str) UTC gece yarısı
// sayar ve UTC'nin gerisindeki tarayıcıda bir önceki günü gösterirdi.
const parseDay = (value) => {
    if (!value) return null;
    const [y, m, d] = String(value).slice(0, 10).split('-').map(Number);
    return Date.UTC(y, m - 1, d);
};

const formatDay = (value) => {
    const t = parseDay(value);
    return t == null ? '-' : new Date(t).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
};

const formatPeriod = (inv) => (inv.periodStart && inv.periodEnd
    ? `${formatDay(inv.periodStart)} – ${formatDay(inv.periodEnd)}`
    : 'Dönemsiz');

const IssuedInvoices = ({ year, week }) => {
    const weekScoped = year != null && week != null;
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [openId, setOpenId] = useState(null);
    const [linkLoadingId, setLinkLoadingId] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await apiService.get(API_ENDPOINTS.FLEET_PANEL.ISSUED_INVOICES);
            const list = Array.isArray(res.data) ? res.data : res.data?.data;
            if (res.success && Array.isArray(list)) setRows(list);
            else { setRows([]); setError(res.error || 'Faturalar yüklenemedi.'); }
        } catch {
            setRows([]);
            setError('Faturalar yüklenemedi.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const visible = useMemo(() => {
        if (!weekScoped) return rows;
        const { start, end } = getWeekDateRange(year, week);
        const from = start.getTime();
        const to = end.getTime();
        return rows.filter(inv => {
            const s = parseDay(inv.periodStart);
            const e = parseDay(inv.periodEnd);
            return s != null && e != null && s <= to && e >= from;
        });
    }, [rows, weekScoped, year, week]);

    const openDocument = async (inv) => {
        setLinkLoadingId(inv.id);
        try {
            const res = await apiService.get(API_ENDPOINTS.FLEET_PANEL.ISSUED_INVOICE_VIEWER_LINK(inv.id));
            const link = typeof res.data === 'string' ? res.data : res.data?.data;
            if (res.success && link) window.open(link, '_blank', 'noopener,noreferrer');
            else fleetToast.error(res.error || 'Fatura görüntülenemedi.');
        } catch {
            fleetToast.error('Fatura görüntülenemedi.');
        } finally {
            setLinkLoadingId(null);
        }
    };

    // Hakediş sekmesindeki kısa görünüm: o haftayı kapsayan fatura yoksa yer kaplamaz.
    if (weekScoped && (loading || error || visible.length === 0)) return null;

    return (
        <section className="isi" aria-label="Hızlıyo'nun size kestiği faturalar">
            <div className="isi-head">
                <h2>{weekScoped ? 'Bu haftayı kapsayan faturalar' : 'Hızlıyo\'nun size kestiği faturalar'}</h2>
                <p>
                    {weekScoped
                        ? 'Hızlıyo\'nun bu dönem için size kestiği fatura. Tutar faturadaki tutardır.'
                        : 'Entegrasyon bedeli ve Hızlıyo\'nun filonuza kestiği diğer faturalar. Bu tutarları siz ödersiniz.'}
                </p>
            </div>

            {error && (
                <Notice tone="error" title="Faturalar yüklenemedi">
                    {error} Bağlantınızı kontrol edip sayfayı yenileyin.
                </Notice>
            )}

            {loading ? (
                <p className="isi-empty">Yükleniyor…</p>
            ) : !error && visible.length === 0 ? (
                <div className="isi-empty">
                    <strong>Size kesilmiş fatura yok</strong>
                    <span>
                        Hızlıyo entegrasyon bedeli faturası kestiğinde burada görünür. Restoranın kontör
                        tükettiği anlaşmalarda (Model 3) fatura kesilmez.
                    </span>
                </div>
            ) : visible.length > 0 && (
                <div className="isi-table-wrap">
                    <table className="isi-table">
                        <thead>
                            <tr>
                                <th>Fatura no</th>
                                <th>Dönem</th>
                                <th className="num">Paket</th>
                                <th className="num">Tutar</th>
                                <th className="num">KDV</th>
                                <th className="num">Ödenecek</th>
                                <th>Durum</th>
                                <th aria-label="İşlemler" />
                            </tr>
                        </thead>
                        <tbody>
                            {visible.map(inv => {
                                const st = STATUS[inv.status] || { label: 'Bilinmiyor', tone: 'info' };
                                const isOpen = openId === inv.id;
                                const voided = inv.status === 4 || inv.status === 5;
                                return (
                                    <Fragment key={inv.id}>
                                        <tr className={voided ? 'is-voided' : ''}>
                                            <td>
                                                <div className="isi-no">{inv.invoiceNumber || 'Numara bekleniyor'}</div>
                                                <div className="isi-sub">
                                                    {DOC_TYPE[inv.documentType] || 'Fatura'}
                                                    {inv.sentAtUtc ? `, ${new Date(inv.sentAtUtc).toLocaleDateString('tr-TR')}` : ''}
                                                </div>
                                            </td>
                                            <td>{formatPeriod(inv)}</td>
                                            <td className="num">{inv.packageCount || '-'}</td>
                                            <td className="num">{formatTL(inv.subtotal)}</td>
                                            <td className="num">{formatTL(inv.vatTotal)}</td>
                                            <td className="num strong">{formatTL(inv.total)}</td>
                                            <td><span className={`status-badge ${st.tone}`}>{st.label}</span></td>
                                            <td>
                                                <div className="isi-actions">
                                                    {inv.lines?.length > 0 && (
                                                        <button
                                                            type="button"
                                                            className="isi-btn"
                                                            aria-expanded={isOpen}
                                                            onClick={() => setOpenId(isOpen ? null : inv.id)}
                                                        >
                                                            <ChevronDown size={14} className={isOpen ? 'is-flipped' : ''} />
                                                            Kalemler
                                                        </button>
                                                    )}
                                                    {inv.hasDocument && (
                                                        <button
                                                            type="button"
                                                            className="isi-btn"
                                                            disabled={linkLoadingId === inv.id}
                                                            onClick={() => openDocument(inv)}
                                                        >
                                                            {linkLoadingId === inv.id
                                                                ? <Loader2 size={14} className="spinning" />
                                                                : <ExternalLink size={14} />}
                                                            Faturayı aç
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                        {isOpen && (
                                            <tr className="isi-lines-row">
                                                <td colSpan={8}>
                                                    <table className="isi-lines">
                                                        <thead>
                                                            <tr>
                                                                <th>Kalem</th>
                                                                <th className="num">Adet</th>
                                                                <th className="num">Birim fiyat</th>
                                                                <th className="num">KDV oranı</th>
                                                                <th className="num">Tutar</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {inv.lines.map((l, i) => (
                                                                <tr key={i}>
                                                                    <td>{l.description}</td>
                                                                    <td className="num">{Number(l.quantity).toLocaleString('tr-TR')}</td>
                                                                    <td className="num">{formatTL(l.unitPrice)}</td>
                                                                    <td className="num">%{Number(l.vatRate).toLocaleString('tr-TR')}</td>
                                                                    <td className="num">{formatTL(l.lineNet)}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                    {voided && inv.cancelReason && (
                                                        <p className="isi-reason">Gerekçe: {inv.cancelReason}</p>
                                                    )}
                                                </td>
                                            </tr>
                                        )}
                                    </Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </section>
    );
};

export default IssuedInvoices;
